import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseFilterComponent } from './base-filter/base-filter.component';
import { BaseService } from 'src/app/tabms/services/base/base.service';
import { ExportOptions, IActualSystemMenuPermission, LocalFormData } from 'src/app/tabms/models/base/base';
import { ActivatedRoute, Router } from '@angular/router';
import { UserFavouritePageService } from 'src/app/tabms/services/global/user-favourite-page.service';
import { ADDTOFAV, DELETESUCCESS, OPERERROR, REMOVEVEF, SAVESUCCESS, TabConst } from 'src/app/tabms/tab-consts';
import { CommandService } from 'src/app/tabms/services/global/command.service';
import { PermissionsListService } from 'src/app/tabms/services/global/permissions-list.service';
import { Subject, takeUntil } from 'rxjs';
import { TabSystemMenuService } from 'src/app/tabms/services/menu/tab-system-menu.service';
import { SessionWrapperService } from 'src/app/tabms/services/session/session-wrapper.service';
import { de } from 'date-fns/locale';

type ViewMode = 'grid' | 'cards' | 'map';
interface ViewConfig {
  mode: ViewMode;
  icon: string;
  labelKey: string;
}

@Component({
  selector: 'app-base-page-tools',
  templateUrl: './base-page-tools.component.html',
  styleUrl: './base-page-tools.component.scss'
})
export class BasePageToolsComponent implements OnInit {
  //#region inputs
  @Input() set service(_service) {
    if (!_service) return;
    this.serviceInstance = _service;
  }

  @Input() set fieldsConfigService(_fieldsConfigService) {
    if (!_fieldsConfigService) return;
    this.fieldsConfigServiceInstance = _fieldsConfigService;
  }

  @Input() canCreate:boolean =true;
  @Input() size: 'default' | 'compact' | 'xs' = TabConst.DefaultSize;
  @Input() availableViews: ViewConfig[] = [];
  @Input() currentView: ViewMode = 'grid';
  @Output() viewModeChange = new EventEmitter<ViewMode>();
  //#endregion

  userId;
  systemMenuId;
  userFavouritePageId;
  isFavourite = false;
  favColor = '#000000';
  printListFields;
  //#region public prop
  serviceInstance;
  fieldsConfigServiceInstance;
  selectedOption: string;
  gridDataList: any = [];
  showRestoreBtn: boolean;
  extraDynamicMenu;
  exportOptions: ExportOptions[];
  screen: string;
  permissions: IActualSystemMenuPermission;
  menuKey: string;
  pageParameter: string;
  private destroy$: Subject<void> = new Subject<void>();

  //#endregion

  //#region constructor
  constructor(
    public dialog: MatDialog,
    private baseService: BaseService,
    private _router: Router,
    private activatedRoute: ActivatedRoute,
    private userFavouritePageService: UserFavouritePageService,
    private commandService: CommandService,
    private permissionListService: PermissionsListService,
    private tabSystemMenuService: TabSystemMenuService,
    private sessionService: SessionWrapperService
  ) {
    this.screen = activatedRoute.snapshot.url[0].path;
  }
  //#endregion

  //#region life hooks
  ngOnInit() {

    this.menuKey = this.sessionService.getMenuKey();
    this.pageParameter = this.sessionService.getPageParameter();

    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.systemMenuId = params['id'];
    })

    this.permissionListService
      .getPagePermissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.permissions = res;
          this.setOptions();
        }
      });

    this.fieldsConfigServiceInstance.printColumnsSubject.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.printListFields = value;
      if (!this.permissions?.print && this.permissions?.export) {
        if (this.printListFields.length == 0) {
          this.exportOptions = [
            { value: 'temp', viewValue: 'downloadTemplate' },
            { value: 'uploadExcel', viewValue: 'uploadExcel' }
          ];
        }
      }
    });
    this.initRestoreButtonFromLocal();
    this.baseService.saveRetrieveLocalData.pipe(takeUntil(this.destroy$)).subscribe((value: number) => {
      const localDataArray: LocalFormData[] = JSON.parse(localStorage.getItem('localFormData') || '[]');
      const localData = localDataArray.find((x) => x.screen == this.screen);
      this.showRestoreBtn = localData == null || localData == undefined ? false : localData.showrestorebtn;
    });

    this.userFavouritePageService.getUserFavouritePages()?.pipe(takeUntil(this.destroy$)).subscribe(
      (res) => {
        let userFavPages = res.result;
        if (userFavPages) {
          this.userFavouritePageId = userFavPages.find((x) => x.tabsystemmenuid == this.systemMenuId)?.id;
          this.isFavourite = this.userFavouritePageId ? true : false;
          this.favColor = this.isFavourite ? '#ddcd0a' : '#000000';
        }
      },
      (err) => {
        this.isFavourite = false;
      }
    );

    this.baseService.clearPageSelectedOption.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.selectedOption = '';
    });

    this.getExtraDynamicMenu();
  }
  //#endregion

  //#region filter
  setOptions() {
    if (this.permissions?.print && this.permissions?.export) {
      this.exportOptions = [
        { value: 'excel', viewValue: 'exportExcel' },
        { value: 'csv', viewValue: 'exportCSV' },
        { value: 'pdf', viewValue: 'exportPdf' },
        { value: 'temp', viewValue: 'downloadTemplate' },
        { value: 'uploadExcel', viewValue: 'uploadExcel' }
      ];
    } else if (this.permissions?.print && !this.permissions?.export) {
      this.exportOptions = [
        { value: 'excel', viewValue: 'exportExcel' },
        { value: 'csv', viewValue: 'exportCSV' },
        { value: 'pdf', viewValue: 'exportPdf' }
      ];
    } else if (!this.permissions?.print && this.permissions?.export) {
      this.exportOptions = [
        { value: 'temp', viewValue: 'downloadTemplate' },
        { value: 'uploadExcel', viewValue: 'uploadExcel' }
      ];
    }
  }

  showFilterPopup() {
    this.dialog.open(BaseFilterComponent, {
      width: '800px',
      data: { service: this.serviceInstance, fieldsConfigServiceInstance: this.fieldsConfigServiceInstance },
      direction: this.baseService.getDirection()
    });
  }
  //#endregion

  //#region Search
  applySearch(event: Event) {
    const searchValue = (event.target as HTMLInputElement).value;
    this.baseService.searchValueChange.next(searchValue);
  }
  //#endregion

  //#region Export Options
  exportOptionsSelect(selectedOption: string) {
    if (selectedOption.startsWith('Command_')) this.baseService.pageToolsDynamicMenuChange.next(selectedOption.split('_')[1]);
    else this.baseService.exportOptionsChange.next(selectedOption);
  }

  //#endregion

  //#region create
  showCreatePopup() {
    //
    /*  this.dialog.open(BaseCreateComponent, {
       width: '800px'
     }); */
    this.baseService.createFormDisplay.next({ show: true, id: null });
  }
  //#endregion

  //#region favourite
  favouriteClick() {
    if (this.isFavourite) {
      this.favColor = '#000000';
      this.removeFavourite();
    } else {
      this.favColor = '#ddcd0a';
      this.addFavourite();
    }
    this.isFavourite = !this.isFavourite;
  }

  addFavourite() {
    let claims = JSON.parse(sessionStorage.getItem('id_token_claims_obj'));
    if (claims) {
      this.userId = claims['sub'];
    }
    let userFavourite = {
      identityuserid: this.userId,
      tabsystemmenuid: this.systemMenuId,
      menukey: this.menuKey,
      pageparameter: this.pageParameter
    };
    this.userFavouritePageService.create(userFavourite)?.pipe(takeUntil(this.destroy$)).subscribe(
      (res) => {
        this.userFavouritePageId = res?.result?.id;
        this.baseService.showSuccessMessage(ADDTOFAV);
        this.baseService.addRemoveFavourite.next(1);
      },
      (err) => {
        this.baseService.showErrorMessage(OPERERROR);
      });
  }

  removeFavourite() {
    this.userFavouritePageService.delete(this.userFavouritePageId)?.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.baseService.showSuccessMessage(REMOVEVEF);
        this.baseService.addRemoveFavourite.next(2);
      },
      error: (err) => {
        this.baseService.showErrorMessage(OPERERROR);
      }
    });
  }
  //#endregion

  //#region restore
  restore() {
    this.baseService.saveRetrieveLocalData.next(2);
    this.baseService.createFormDisplay.next({ show: true, id: '' });
  }
  //#endregion

  //#region dynamic methods
  getExtraDynamicMenu() {
    this.commandService.getAll().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res.result && res.result.length > 0) {
          this.extraDynamicMenu = res.result.filter((x) => x.tabsystemmenuid == this.systemMenuId && x.type == 1);
        }
      },
      error: (err) => {
      }
    });
  }

  //#endregion

  onOptionClicked(value) {
    this.exportOptionsSelect(value);
  }

  onViewModeClick(mode: ViewMode): void {
    if (this.currentView === mode) return;
    this.viewModeChange.emit(mode);
  }

  // Initialize showRestoreBtn from local storage on component load first
  private initRestoreButtonFromLocal() {
    const localDataArray: LocalFormData[] = JSON.parse(localStorage.getItem('localFormData') || '[]');
    const localData = localDataArray.find((x) => x.screen == this.screen);
    this.showRestoreBtn = localData == null || localData == undefined ? false : !!localData.showrestorebtn;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
